import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api';
import { errorText, num, t } from '../i18n';
import { canConfigureReminders, DEFAULT_TIME, deviceState, presetOf, PRESETS, validTime, type DeviceFacts, type DeviceState, type ReminderSettings } from '../reminder-state';
import { forgetDevice, readFacts, turnOff, turnOn } from '../reminders';
import { currentOwner, resolveSession } from '../session';
import { hashOpen } from './home-hash';

/**
 * Reminders (v1 ticket 38), opened from home's ⋯ menu as a `#reminders` history entry over the town: what the
 * browser's push service sees, the learner's five minutes (one time for all their devices), whether a reminder may
 * ask one of their words, and this phone's state with the button that matches. The server says what is on
 * (`GET /api/reminders`); the phone says what it can do (`reminders.ts`); `deviceState` decides what to show.
 *
 * Like the report sheet it is a history entry on the same path, so Back closes it; `reminders-open` on <html> makes
 * home's bar and panel inert and fades the town's music (`HOME_OVERLAYS`).
 */
const HASH = '#reminders';

/** Opens the sheet over the current screen (once). */
export function openReminders() {
  if (document.querySelector('reminder-sheet')) return;
  const el = document.createElement('reminder-sheet') as ReminderSheet;
  el.from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  document.body.append(el);
}

/** Opened straight at `/#reminders` (a reload, the first-round offer). */
export const remindersInUrl = () => hashOpen(HASH);

@customElement('reminder-sheet')
export class ReminderSheet extends LitElement {
  /** Focus goes back here when the sheet closes. */
  from: HTMLElement | null = null;
  @state() private server: ReminderSettings | null = null;
  @state() private facts: DeviceFacts | null = null;
  @state() private other = false;
  @state() private busy = false;
  @state() private note: 'checking' | 'fellBack' | 'denied' | 'cant' | '' = '';
  @state() private error = '';
  @state() private loadFailed = false;
  @state() private online = navigator.onLine !== false;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    if (location.hash !== HASH) history.pushState({ reminders: true }, '', `${location.pathname}${location.search}${HASH}`);
    document.documentElement.classList.add('reminders-open');
    window.addEventListener('popstate', this.onPop);
    window.addEventListener('focus', this.onReturn);
    window.addEventListener('online', this.onConnection);
    window.addEventListener('offline', this.onConnection);
    document.addEventListener('visibilitychange', this.onReturn);
    // Capture, so Escape closes this sheet and not whatever is under it.
    window.addEventListener('keydown', this.onKey, true);
    void this.load().then(() => this.updateComplete).then(() => this.querySelector<HTMLElement>('.reminder-close')?.focus());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.documentElement.classList.remove('reminders-open');
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('focus', this.onReturn);
    window.removeEventListener('online', this.onConnection);
    window.removeEventListener('offline', this.onConnection);
    document.removeEventListener('visibilitychange', this.onReturn);
    window.removeEventListener('keydown', this.onKey, true);
  }

  private onPop = () => {
    if (location.hash !== HASH) this.shut();
  };

  /** Browser notification permission can change in site settings while this sheet is open. */
  private onReturn = () => {
    if (document.visibilityState !== 'visible' || !this.server) return;
    void readFacts(this.server.devices).then((facts) => {
      if (!this.isConnected) return;
      this.facts = facts;
      if (this.note === 'denied' && facts.permission !== 'denied') this.note = '';
    });
  };

  private onConnection = () => { this.online = navigator.onLine !== false; };

  private onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    this.close();
  };

  private close = () => {
    if ((history.state as { reminders?: boolean } | null)?.reminders) {
      history.back();
      return;
    }
    history.replaceState(null, '', `${location.pathname}${location.search}`);
    this.shut();
  };

  private shut() {
    const back = this.from;
    this.remove();
    queueMicrotask(() => { if (back?.isConnected) back.focus(); });
  }

  private async load() {
    try {
      const s = await api.reminders();
      this.server = s;
      this.other = presetOf(s.time) === 'other';
      // The remembered device is kept per owner, and a cold load has no owner yet.
      if (!currentOwner()) await resolveSession();
      this.facts = await readFacts(s.devices);
      this.loadFailed = false;
    } catch (e) {
      this.loadFailed = true;
      this.error = errorText(e);
    }
  }

  private retryLoad = () => void this.run(() => this.load());

  private checkAgain = () => void this.run(async () => {
    if (this.server) {
      this.facts = await readFacts(this.server.devices);
      if (this.facts.permission !== 'denied') this.note = '';
    }
  });

  /** Reads the server again and this phone's facts with it. */
  private async refresh(s?: ReminderSettings) {
    this.server = s ?? await api.reminders();
    this.facts = await readFacts(this.server.devices);
  }

  private run = async (fn: () => Promise<void>) => {
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      await fn();
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.busy = false;
    }
  };

  private saveTime = (time: string) => {
    if (!validTime(time) || time === this.server?.time) return;
    void this.run(async () => { await this.refresh(await api.setReminders({ time })); });
  };

  private pick = (id: string) => {
    if (id === 'other') {
      this.other = true;
      return;
    }
    this.other = false;
    const p = PRESETS.find((x) => x.id === id);
    if (p) this.saveTime(p.time);
  };

  private setCard = (card: boolean) => {
    void this.run(async () => { await this.refresh(await api.setReminders({ card })); });
  };

  private setRaidAlerts = (raidAlerts: boolean) => {
    void this.run(async () => { await this.refresh(await api.setReminders({ raidAlerts })); });
  };

  private on = () => void this.run(async () => {
    const s = this.server;
    if (!s || !this.online) return;
    this.note = '';
    forgetDevice();
    const r = await turnOn(s.publicKey, () => { this.note = 'checking'; });
    this.note = r === 'on-sync' ? 'fellBack' : r === 'denied' ? 'denied' : r === 'cant' ? 'cant' : ''  /* dismissed: no line */;
    await this.refresh();
  });

  private off = () => void this.run(async () => {
    this.note = '';
    await turnOff();
    await this.refresh();
  });

  private resume = () => void this.run(async () => { await this.refresh(await api.resumeReminders()); });

  render() {
    return html`
      <div class="moments-dim reminder-dim" aria-hidden="true" @click=${this.close}></div>
      <section class="moments-panel reminder-panel" role="dialog" aria-modal="true" aria-labelledby="reminder-title">
        <div class="moments-head">
          <h2 id="reminder-title">${t('reminder.title')}</h2>
          <button class="btn ghost icon reminder-close" @click=${this.close} aria-label=${t('reminder.close')}>✕</button>
        </div>
        <p class="reminder-privacy small muted">${t('reminder.privacy')}</p>
        ${this.server ? this.renderSettings(this.server) : this.loadFailed
          ? html`<button class="btn block reminder-retry" ?disabled=${this.busy} @click=${this.retryLoad}>${t('reminder.tryAgain')}</button>`
          : html`<span class="spinner" role="status" aria-label=${t('reminder.state.checkingCapabilities')}></span>`}
        ${this.error ? html`<p class="report-error reminder-error" role="alert">${this.error}</p>` : nothing}
      </section>`;
  }

  private renderSettings(s: ReminderSettings) {
    const ready = this.facts !== null;
    const canConfigure = this.online && ready && canConfigureReminders({ ...this.facts!, devices: s.devices });
    return html`
      ${!this.online ? html`<p class="reminder-offline" role="status">${t('reminder.offline')}</p>` : nothing}
      ${ready ? this.renderDevice(s) : html`<p class="reminder-state" role="status">${t('reminder.state.checkingCapabilities')}</p>`}
      ${ready && canConfigure ? this.renderPreferences(s) : ready && this.online ? html`<p class="reminder-state reminder-setup-hint" role="status">${t('reminder.setupAfterCapability')}</p>` : nothing}
    `;
  }

  private renderPreferences(s: ReminderSettings) {
    const preset = this.other ? 'other' : presetOf(s.time);
    return html`
      <fieldset class="reminder-when" ?disabled=${this.busy}>
        <legend>${t('reminder.when')}</legend>
        ${PRESETS.map((p) => html`<label class="reminder-radio">
          <input type="radio" name="reminder-time" value=${p.id} .checked=${preset === p.id} @change=${() => this.pick(p.id)} />
          <span>${t(`reminder.preset.${p.id}`)}${t('common.dot')}<bdi dir="ltr" class="reminder-clock">${this.clock(p.time)}</bdi></span>
        </label>`)}
        <label class="reminder-radio">
          <input type="radio" name="reminder-time" value="other" .checked=${preset === 'other'} @change=${() => this.pick('other')} />
          <span>${t('reminder.preset.other')}</span>
        </label>
        ${preset === 'other'
          ? html`<input type="time" class="reminder-time" aria-label=${t('reminder.timeAria')} .value=${validTime(s.time) ? s.time : DEFAULT_TIME}
              @change=${(e: Event) => this.saveTime((e.target as HTMLInputElement).value)} />`
          : nothing}
      </fieldset>
      <label class="report-include reminder-card">
        <input type="checkbox" .checked=${s.card} ?disabled=${this.busy} @change=${(e: Event) => this.setCard((e.target as HTMLInputElement).checked)} />
        <span>${t('reminder.card')}<span class="muted small">${t('reminder.cardHint')}</span></span>
      </label>
      ${s.raidAlerts === undefined ? nothing : html`<label class="report-include reminder-card">
        <input type="checkbox" .checked=${s.raidAlerts} ?disabled=${this.busy} @change=${(e: Event) => this.setRaidAlerts((e.target as HTMLInputElement).checked)} />
        <span>${t('reminder.raidAlerts')}<span class="muted small">${t('reminder.raidAlertsHint')}</span></span>
      </label>`}
    `;
  }

  /** "08:00" as the interface writes it (Persian digits in Persian). */
  private clock(time: string) {
    return time.replace(/\d+/g, (d) => num(d));
  }

  private renderDevice(s: ReminderSettings) {
    const state: DeviceState | 'loading' = this.facts ? deviceState({ ...this.facts, devices: s.devices }) : 'loading';
    const mine = state === 'on-push' || state === 'on-sync';
    const line = (key: string) => html`<p class="reminder-state" role="status">${t(key)}</p>`;
    const button = (cls: string, key: string, fn: () => void, primary = true) =>
      html`<button class="btn ${primary ? 'primary' : ''} block ${cls}" ?disabled=${this.busy || (!this.online && fn === this.on)} @click=${fn}>${this.busy ? html`<span class="spinner"></span>` : t(key)}</button>`;
    return html`
      <div class="reminder-device" data-state=${state}>
        ${s.paused ? html`<p class="reminder-paused" role="status">${t('reminder.state.paused')}</p>${button('reminder-resume', 'reminder.turnBack', this.resume)}` : nothing}
        ${this.note === 'checking' ? line('reminder.state.checking')
          : this.note === 'fellBack' ? line('reminder.state.fellBack')
          : this.note === 'denied' ? line('reminder.state.denied')
          : this.note === 'cant' ? line('reminder.state.unsupported')
          : nothing}
        ${state === 'unsupported' && !this.note ? line('reminder.state.unsupported') : nothing}
        ${state === 'ios' ? line('reminder.state.ios') : nothing}
        ${state === 'denied' && !this.note ? line('reminder.state.denied') : nothing}
        ${state === 'stopped' ? line('reminder.state.stopped') : nothing}
        ${state === 'denied' ? button('reminder-recheck', 'reminder.checkAgain', this.checkAgain, false) : nothing}
        ${state === 'on-push' && !this.note ? line('reminder.state.on') : nothing}
        ${state === 'on-sync' && this.note !== 'fellBack' ? line('reminder.state.onSync') : nothing}
        ${state === 'off' || state === 'stopped' ? button('reminder-on', 'reminder.turnOn', this.on) : nothing}
        ${mine ? button('reminder-off', 'reminder.turnOff', this.off, false) : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'reminder-sheet': ReminderSheet }
}
